// components/dashboard/InboxTab.jsx
// WhatsApp-web style inbox: left list of conversations, right message pane.
// Backed by /messages/* on the shared API. Palette matches the Home tab.
import { useEffect, useMemo, useRef, useState } from "react"
import { ArrowLeft, Search, MessageSquare, ArrowUp, Zap, Clock, Inbox } from "lucide-react"
import toast from "react-hot-toast"
import { doc as fsDoc, getDoc, setDoc, collection, getDocs } from "firebase/firestore"
import { db } from "../../firebaseConfig"

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
export const API_BASE = "https://jobscanner-e1dd4.ue.r.appspot.com"

// ── Palette (matches Home / WildTab shared palette) ──────────────────────
const C = {
  bg:         "#F7F7F8",
  surface:    "#FFFFFF",
  surfaceAlt: "#FAFAFA",
  border:     "#E4E4E7",
  divider:    "#EEEEEE",
  ink:        "#18181b",
  ink2:       "#27272a",
  inkMuted:   "#52525b",
  inkDim:     "#71717a",
  inkFaint:   "#a1a1aa",
  accent:     "#18181b",  // read-receipt / primary (dark ink)
  unread:     "#2563EB",  // small blue unread cue
  danger:     "#DC2626",
  pillBg:     "#F4F4F5",
}

// ── Helpers ───────────────────────────────────────────────────────────────
function parseDate(s) {
  if (!s) return null
  const t = new Date(s).getTime()
  return Number.isFinite(t) ? new Date(t) : null
}

function agoShort(dt) {
  if (!dt) return ""
  const diff = Date.now() - dt.getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return "now"
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d`
  return `${Math.floor(d / 7)}w`
}

function timeLabel(dt) {
  if (!dt) return ""
  const now = new Date()
  const sameDay = dt.toDateString() === now.toDateString()
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1)
  const isYesterday = dt.toDateString() === yesterday.toDateString()
  const diffDays = Math.floor((now - dt) / 86400000)
  const timeStr = dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
  if (sameDay) return timeStr
  if (isYesterday) return "Yesterday"
  if (diffDays < 7) return ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][dt.getDay()]
  return dt.toLocaleDateString()
}

function dateSeparator(dt) {
  if (!dt) return ""
  const now = new Date()
  const sameDay = dt.toDateString() === now.toDateString()
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1)
  if (sameDay) return "Today"
  if (dt.toDateString() === yesterday.toDateString()) return "Yesterday"
  const diffDays = Math.floor((now - dt) / 86400000)
  if (diffDays < 7) return ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][dt.getDay()]
  return dt.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
}

function sameDay(a, b) {
  if (!a || !b) return false
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth() === b.getMonth() &&
         a.getDate() === b.getDate()
}

function initialsOf(name = "") {
  return name.trim().split(/[\s_\-.]+/).filter(Boolean).slice(0, 2)
    .map(w => w[0]?.toUpperCase() || "").join("") || "?"
}

export function normalizeConv(j) {
  return {
    id: j.conversationId || j.id || "",
    companyName:   j.companyName   || "",
    logoUrl:       j.logoUrl       || "",
    recruiterName: j.recruiterName || "",
    recruiterId:   j.recruiterId   || "",
    candidateId:   j.candidateId   || "",
    lastMessage:   j.lastMessage   || "",
    lastMessageBy: j.lastMessageBy || "",
    lastMessageAt: parseDate(j.lastMessageAt) || new Date(0),
  }
}

export function normalizeMsg(j) {
  return {
    id: j.id || "",
    senderId: j.senderId || "",
    text: j.text || "",
    sentAt: parseDate(j.sentAt) || new Date(),
    pending: Boolean(j.pending),
  }
}

// ── Company logo ──────────────────────────────────────────────────────────
function CompanyLogo({ company, logoUrl, size = 44 }) {
  const [failed, setFailed] = useState(false)
  const show = !!logoUrl && !failed
  const radius = Math.round(size * 0.26)
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, flexShrink: 0,
      background: C.pillBg, border: `1px solid ${C.border}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden",
    }}>
      {show ? (
        <img src={logoUrl} alt="" referrerPolicy="no-referrer"
             onError={() => setFailed(true)}
             style={{ width: "100%", height: "100%", objectFit: "contain", background: "#fff" }} />
      ) : (
        <span style={{
          fontSize: Math.round(size * 0.32), fontWeight: 800, color: C.ink,
          letterSpacing: "-0.03em", fontFamily: FF,
        }}>
          {initialsOf(company)}
        </span>
      )}
    </div>
  )
}

// ── Conversation row ──────────────────────────────────────────────────────
function ConversationRow({ conv, currentUid, isActive, isUnread, onClick }) {
  const mine = conv.lastMessageBy === currentUid
  const firstName = (conv.recruiterName || "").split(" ")[0] || "They"
  const snippet = mine
    ? `You: ${conv.lastMessage}`
    : `${firstName}: ${conv.lastMessage}`
  return (
    <button
      className="inbox-row"
      onClick={onClick}
      style={{
        position: "relative",
        display: "flex", width: "100%", gap: 12, alignItems: "center",
        padding: "11px 16px 11px 20px",
        background: isActive ? C.pillBg : "transparent",
        border: "none", cursor: "pointer",
        textAlign: "left", fontFamily: FF,
        transition: "background 0.1s",
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = C.surfaceAlt }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent" }}
    >
      {isActive && (
        <span style={{
          position: "absolute", left: 0, top: 10, bottom: 10, width: 2,
          background: C.ink, borderRadius: 2,
        }} />
      )}
      <CompanyLogo company={conv.companyName} logoUrl={conv.logoUrl} size={42} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            flex: 1, minWidth: 0,
            fontSize: 13.5, fontWeight: isUnread ? 700 : 600, color: C.ink,
            letterSpacing: "-0.005em",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {conv.recruiterName || "Someone"}
          </span>
          <span style={{
            fontSize: 11,
            color: isUnread ? C.unread : C.inkFaint,
            fontWeight: isUnread ? 600 : 500, flexShrink: 0,
            letterSpacing: 0.1,
          }}>
            {agoShort(conv.lastMessageAt)}
          </span>
        </div>
        <div style={{
          marginTop: 3, display: "flex", alignItems: "center", gap: 6,
        }}>
          <span style={{
            flex: 1, minWidth: 0,
            fontSize: 12.5, lineHeight: 1.4,
            color: isUnread ? C.ink2 : C.inkDim,
            fontWeight: isUnread ? 500 : 400,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {snippet}
          </span>
          {isUnread && (
            <span style={{
              width: 7, height: 7, borderRadius: "50%", background: C.unread, flexShrink: 0,
            }} />
          )}
        </div>
        {conv.companyName && (
          <div style={{
            marginTop: 2, fontSize: 10.5, color: C.inkFaint,
            fontWeight: 500, letterSpacing: "0.02em",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {conv.companyName}
          </div>
        )}
      </div>
    </button>
  )
}

// ── Chat bubble ───────────────────────────────────────────────────────────
function Bubble({ msg, mine }) {
  return (
    <div style={{
      display: "flex", justifyContent: mine ? "flex-end" : "flex-start",
      marginBottom: 6,
    }}>
      <div style={{
        maxWidth: "min(72%, 540px)",
        display: "flex", flexDirection: "column",
        alignItems: mine ? "flex-end" : "flex-start",
      }}>
        <div style={{
          padding: "10px 14px",
          background: mine ? C.ink : C.surface,
          color: mine ? "#fff" : C.ink,
          border: mine ? "none" : `1px solid ${C.border}`,
          borderRadius: mine ? "18px 4px 18px 18px" : "4px 18px 18px 18px",
          fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap",
          opacity: msg.pending ? 0.7 : 1,
        }}>
          {msg.text}
        </div>
        <div style={{
          fontSize: 10, color: C.inkFaint, marginTop: 3,
        }}>
          {timeLabel(msg.sentAt)}
          {msg.pending ? " · sending…" : ""}
        </div>
      </div>
    </div>
  )
}

// ── Date separator ────────────────────────────────────────────────────────
function DateChip({ dt }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      margin: "16px 0",
    }}>
      <div style={{ flex: 1, height: 1, background: C.divider }} />
      <span style={{
        fontSize: 11, color: C.inkFaint, fontWeight: 600, fontFamily: FF,
        padding: "3px 10px", background: C.surfaceAlt, borderRadius: 999,
        border: `1px solid ${C.border}`,
      }}>
        {dateSeparator(dt)}
      </span>
      <div style={{ flex: 1, height: 1, background: C.divider }} />
    </div>
  )
}

// ── Right-pane: conversation view ─────────────────────────────────────────
function ConversationView({ conv, currentUid, msgsCache, setMsgsCache, onBack, isMobile }) {
  const [messages, setMessages] = useState(() => msgsCache[conv.id] || null)
  const [loading, setLoading] = useState(messages === null)
  const [error, setError] = useState(null)
  const [text, setText] = useState("")
  const [sending, setSending] = useState(false)
  const scrollRef = useRef(null)

  const scrollToBottom = (behavior = "smooth") => {
    const el = scrollRef.current
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior })
    })
  }

  // Load messages (cache-first, then background refresh)
  useEffect(() => {
    let active = true
    const load = async () => {
      const cached = msgsCache[conv.id]
      if (cached) {
        setMessages(cached); setLoading(false)
        requestAnimationFrame(() => scrollToBottom("auto"))
      } else {
        setLoading(true)
      }
      try {
        const res = await fetch(
          `${API_BASE}/messages/conversations/${conv.id}/messages?uid=${encodeURIComponent(currentUid)}`
        )
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const body = await res.json()
        const list = (body.messages || []).map(normalizeMsg)
        if (!active) return
        setMessages(list)
        setMsgsCache(prev => ({ ...prev, [conv.id]: list }))
        setLoading(false)
        setError(null)
        requestAnimationFrame(() => scrollToBottom("auto"))
      } catch (err) {
        console.error("Fetch messages failed:", err)
        if (!active) return
        if (!cached) {
          setLoading(false); setError(String(err))
        }
      }
    }
    load()
    return () => { active = false }
  }, [conv.id, currentUid])

  const send = async () => {
    const body = text.trim()
    if (!body || sending) return
    const now = new Date()
    const pending = {
      id: `pending-${now.getTime()}`,
      senderId: currentUid,
      text: body, sentAt: now, pending: true,
    }
    setText("")
    setSending(true)
    setMessages(prev => {
      const next = [...(prev || []), pending]
      setMsgsCache(c => ({ ...c, [conv.id]: next }))
      return next
    })
    scrollToBottom()
    try {
      const res = await fetch(`${API_BASE}/messages/conversations/${conv.id}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ senderId: currentUid, text: body }),
      })
      if (res.status !== 201) throw new Error(`HTTP ${res.status}`)
      // Replace pending with the server echo (fetch latest)
      try {
        const r2 = await fetch(
          `${API_BASE}/messages/conversations/${conv.id}/messages?uid=${encodeURIComponent(currentUid)}`
        )
        if (r2.ok) {
          const b2 = await r2.json()
          const list = (b2.messages || []).map(normalizeMsg)
          setMessages(list)
          setMsgsCache(c => ({ ...c, [conv.id]: list }))
          scrollToBottom()
        }
      } catch {}
    } catch (err) {
      console.error("Send failed:", err)
      toast.error("couldn’t send. try again.")
      // roll back the pending message
      setMessages(prev => {
        const next = (prev || []).filter(m => m.id !== pending.id)
        setMsgsCache(c => ({ ...c, [conv.id]: next }))
        return next
      })
      setText(body)
    } finally {
      setSending(false)
    }
  }

  const firstName = (conv.recruiterName || "").split(" ")[0] || "them"
  const canSend = text.trim().length > 0 && !sending

  return (
    <section style={{
      display: "flex", flexDirection: "column", height: "100%",
      background: C.bg, minHeight: 0, minWidth: 0,
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 20px", borderBottom: `1px solid ${C.border}`,
        background: C.surface, flexShrink: 0,
      }}>
        {isMobile && (
          <button onClick={onBack} aria-label="back" style={{
            width: 30, height: 30, borderRadius: 8, border: "none",
            background: C.surfaceAlt, cursor: "pointer", color: C.ink,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <ArrowLeft size={16} />
          </button>
        )}
        <CompanyLogo company={conv.companyName} logoUrl={conv.logoUrl} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: C.ink, letterSpacing: "-0.01em",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {conv.recruiterName || "Conversation"}
          </div>
          {conv.companyName && (
            <div style={{
              fontSize: 11.5, color: C.inkDim, marginTop: 1,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {conv.companyName}
            </div>
          )}
        </div>
      </div>

      {/* Messages scroll */}
      <div ref={scrollRef} style={{
        flex: 1, minHeight: 0, overflowY: "auto",
        padding: "18px 24px 10px", background: C.bg,
      }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <span className="inbox-spinner" style={{
              width: 22, height: 22, borderRadius: "50%",
              border: `2px solid ${C.border}`, borderTopColor: C.ink,
            }} />
          </div>
        ) : error ? (
          <div style={{ padding: 40, textAlign: "center", color: C.inkFaint, fontSize: 13 }}>
            couldn’t load messages.
          </div>
        ) : !messages || messages.length === 0 ? (
          <div style={{ padding: 60, textAlign: "center", color: C.inkFaint, fontSize: 13 }}>
            no messages yet — say hi.
          </div>
        ) : (
          messages.map((m, i) => {
            const prev = i > 0 ? messages[i - 1] : null
            const showSep = !prev || !sameDay(prev.sentAt, m.sentAt)
            return (
              <div key={m.id}>
                {showSep && <DateChip dt={m.sentAt} />}
                <Bubble msg={m} mine={m.senderId === currentUid} />
              </div>
            )
          })
        )}
      </div>

      {/* Compose */}
      <div style={{
        padding: "10px 20px 14px", borderTop: `1px solid ${C.border}`,
        background: C.surface, flexShrink: 0,
      }}>
        <form
          onSubmit={e => { e.preventDefault(); send() }}
          style={{
            display: "flex", alignItems: "flex-end", gap: 8,
            padding: "4px 4px 4px 14px",
            background: C.surfaceAlt, borderRadius: 10,
            border: `1px solid ${C.border}`,
          }}>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault(); send()
              }
            }}
            placeholder={`Reply to ${firstName}…`}
            rows={1}
            style={{
              flex: 1, resize: "none", border: "none", outline: "none",
              background: "transparent", padding: "9px 0",
              fontSize: 13.5, lineHeight: 1.5, color: C.ink, fontFamily: FF,
              minHeight: 20, maxHeight: 140,
            }}
          />
          <button
            type="submit"
            disabled={!canSend}
            aria-label="send"
            style={{
              width: 32, height: 32, borderRadius: 8, border: "none",
              background: canSend ? C.ink : "transparent",
              color: canSend ? "#fff" : C.inkFaint,
              cursor: canSend ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, transition: "background 0.12s, color 0.12s",
            }}>
            {sending ? (
              <span className="inbox-spinner" style={{
                width: 13, height: 13, borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
              }} />
            ) : <ArrowUp size={16} strokeWidth={2.4} />}
          </button>
        </form>
      </div>
    </section>
  )
}

// ── Row skeleton ─────────────────────────────────────────────────────────
function RowSkeleton() {
  return (
    <div style={{
      display: "flex", gap: 12, padding: "11px 16px 11px 20px",
      borderTop: `1px solid ${C.divider}`,
    }}>
      <div style={{ width: 42, height: 42, borderRadius: 11, background: C.surfaceAlt }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7, paddingTop: 4 }}>
        <div style={{ width: "55%", height: 10, borderRadius: 4, background: C.surfaceAlt }} />
        <div style={{ width: "80%", height: 9, borderRadius: 4, background: C.surfaceAlt }} />
        <div style={{ width: "35%", height: 8, borderRadius: 4, background: C.surfaceAlt }} />
      </div>
    </div>
  )
}

// ── Empty state (right pane with no selection) ───────────────────────────
function EmptyPane({ total = 0, unread = 0 }) {
  const tips = [
    { icon: Zap,   title: "Reply fast",     body: "Recruiters move quickly — first replies get interviews." },
    { icon: Clock, title: "Stay in context", body: "Each thread stays grouped by company and role." },
    { icon: Inbox, title: "One inbox",       body: "Every reply from every outreach shows up right here." },
  ]
  return (
    <section style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", height: "100%",
      background: C.bg, padding: 32, fontFamily: FF, position: "relative",
      overflow: "hidden",
    }}>
      {/* Decorative chat preview */}
      <div style={{
        position: "relative", width: 260, height: 128, marginBottom: 24,
      }}>
        {/* Incoming bubble */}
        <div style={{
          position: "absolute", left: 0, top: 0,
          padding: "9px 13px", background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: "4px 16px 16px 16px",
          fontSize: 12, color: C.ink2,
          boxShadow: "0 2px 6px rgba(24,24,27,0.04)",
          transform: "rotate(-2deg)",
        }}>
          Hey — are you available this week?
        </div>
        {/* Outgoing bubble */}
        <div style={{
          position: "absolute", right: 0, top: 56,
          padding: "9px 13px", background: C.ink, color: "#fff",
          borderRadius: "16px 4px 16px 16px",
          fontSize: 12,
          boxShadow: "0 2px 8px rgba(24,24,27,0.12)",
          transform: "rotate(1.5deg)",
        }}>
          Yes, Thursday works 👍
        </div>
      </div>

      <div style={{
        fontSize: 20, fontWeight: 700, color: C.ink,
        letterSpacing: "-0.02em",
      }}>
        {total === 0 ? "Your inbox is quiet" : "Pick a conversation"}
      </div>
      <div style={{
        marginTop: 8, maxWidth: 360, fontSize: 13.5, color: C.inkDim,
        lineHeight: 1.55, textAlign: "center",
      }}>
        {total === 0
          ? "Replies from recruiters and hiring managers will land here as you start reaching out."
          : unread > 0
            ? `You have ${unread} unread ${unread === 1 ? "message" : "messages"} waiting on the left.`
            : "Select a thread on the left to open it up."}
      </div>

      {/* Tip row */}
      <div style={{
        marginTop: 32, display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 180px))",
        gap: 12, width: "100%", maxWidth: 580,
      }}>
        {tips.map(({ icon: Icon, title, body }) => (
          <div key={title} style={{
            padding: "14px 14px 12px",
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 14,
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: C.pillBg,
              display: "flex", alignItems: "center", justifyContent: "center",
              marginBottom: 10,
            }}>
              <Icon size={14} color={C.ink} strokeWidth={2.2} />
            </div>
            <div style={{
              fontSize: 12.5, fontWeight: 700, color: C.ink,
              letterSpacing: "-0.005em",
            }}>
              {title}
            </div>
            <div style={{
              marginTop: 3, fontSize: 11.5, color: C.inkDim,
              lineHeight: 1.4,
            }}>
              {body}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ── Main InboxTab ────────────────────────────────────────────────────────
export default function InboxTab({ userId }) {
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState("")
  const [selectedId, setSelectedId] = useState(null)
  const [readAts, setReadAts] = useState({})          // conv.id → Date
  const [msgsCache, setMsgsCache] = useState({})      // conv.id → [messages]
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined") && window.innerWidth < 768)

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener("resize", h)
    return () => window.removeEventListener("resize", h)
  }, [])

  // Fetch conversations
  const load = async () => {
    if (!userId) { setLoading(false); return }
    try {
      const res = await fetch(`${API_BASE}/messages/inbox?uid=${encodeURIComponent(userId)}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = await res.json()
      const list = (body.conversations || []).map(normalizeConv)
      list.sort((a, b) => b.lastMessageAt - a.lastMessageAt)
      setConversations(list)
      setError(null)
    } catch (err) {
      console.error("Inbox fetch failed:", err)
      setError(String(err))
    } finally { setLoading(false) }
  }

  // Load Firestore read-ats
  const loadReadAts = async () => {
    if (!userId) return
    try {
      const snap = await getDocs(collection(db, "users", userId, "inbox"))
      const next = {}
      snap.forEach(d => {
        const v = d.data()?.readAt
        if (v?.toDate) next[d.id] = v.toDate()
      })
      setReadAts(next)
    } catch (err) {
      console.error("Read-ats load failed:", err)
    }
  }

  useEffect(() => {
    load()
    loadReadAts()
  }, [userId])

  const markRead = async (convId) => {
    const now = new Date()
    setReadAts(prev => ({ ...prev, [convId]: now }))
    if (!userId || !convId) return
    try {
      await setDoc(
        fsDoc(db, "users", userId, "inbox", convId),
        { readAt: now },
        { merge: true },
      )
    } catch (err) {
      console.error("markRead failed:", err)
    }
  }

  const isUnread = (c) => {
    if (!userId) return false
    if (c.lastMessageBy === userId) return false
    const r = readAts[c.id]
    return !r || c.lastMessageAt > r
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return conversations
    return conversations.filter(c => {
      const hay = `${c.recruiterName} ${c.companyName} ${c.lastMessage}`.toLowerCase()
      return hay.includes(q)
    })
  }, [conversations, query])

  const selectedConv = useMemo(
    () => conversations.find(c => c.id === selectedId) || null,
    [conversations, selectedId]
  )

  const unreadTotal = useMemo(() => conversations.filter(isUnread).length,
    // eslint-disable-next-line
    [conversations, readAts, userId])

  const openConversation = (conv) => {
    setSelectedId(conv.id)
    if (isUnread(conv)) markRead(conv.id)
  }

  // ── Render ─────────────────────────────────────────────────────────────
  const leftPane = (
    <aside style={{
      display: "flex", flexDirection: "column", height: "100%",
      background: C.surface, borderRight: `1px solid ${C.border}`,
      minHeight: 0,
    }}>
      <div style={{
        padding: "14px 20px 12px", borderBottom: `1px solid ${C.border}`,
        background: C.surface,
      }}>
        <div style={{
          display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10,
        }}>
          <h2 style={{
            margin: 0, fontSize: 18, fontWeight: 700, color: C.ink,
            letterSpacing: "-0.02em", fontFamily: FF,
          }}>
            Messages
          </h2>
          <span style={{
            fontSize: 12, color: C.inkDim, fontWeight: 500,
          }}>
            {conversations.length === 0 ? "no conversations"
              : unreadTotal > 0
                ? `${conversations.length} · ${unreadTotal} unread`
                : `${conversations.length}`}
          </span>
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 14px", borderRadius: 999,
          background: "#EDEDF0", border: "none",
        }}>
          <Search size={13} color={C.inkDim} strokeWidth={2} />
          <input
            value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search conversations"
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              fontSize: 12.5, color: C.ink, fontFamily: FF,
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="inbox-shimmer">
              <RowSkeleton />
            </div>
          ))
        ) : error ? (
          <div style={{
            padding: 24, textAlign: "center", color: C.inkFaint, fontSize: 13,
            fontFamily: FF,
          }}>
            couldn’t load conversations.
            <div style={{ marginTop: 10 }}>
              <button onClick={() => { setLoading(true); load() }} style={{
                padding: "6px 14px", borderRadius: 8, border: "none",
                background: C.ink, color: "#fff", fontSize: 12, fontWeight: 700,
                cursor: "pointer", fontFamily: FF,
              }}>
                retry
              </button>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            padding: "40px 20px", textAlign: "center",
            color: C.inkFaint, fontSize: 13, fontFamily: FF,
          }}>
            {conversations.length === 0
              ? "no messages yet. replies from recruiters will show up here."
              : "no conversations match that search."}
          </div>
        ) : (
          filtered.map(c => (
            <ConversationRow
              key={c.id}
              conv={c}
              currentUid={userId}
              isActive={c.id === selectedId}
              isUnread={isUnread(c)}
              onClick={() => openConversation(c)}
            />
          ))
        )}
      </div>
    </aside>
  )

  const rightPane = selectedConv ? (
    <ConversationView
      key={selectedConv.id}
      conv={selectedConv}
      currentUid={userId}
      msgsCache={msgsCache}
      setMsgsCache={setMsgsCache}
      onBack={() => setSelectedId(null)}
      isMobile={isMobile}
    />
  ) : <EmptyPane total={conversations.length} unread={unreadTotal} />

  return (
    <div style={{
      display: "flex", flex: 1, minHeight: 0,
      background: C.surface, fontFamily: FF,
    }}>
      <style>{`
        @keyframes inbox-shimmer-anim {
          0% { opacity: 0.55; } 50% { opacity: 1; } 100% { opacity: 0.55; }
        }
        .inbox-shimmer > div > div { animation: inbox-shimmer-anim 1.4s ease-in-out infinite; }
        @keyframes inbox-spin { to { transform: rotate(360deg); } }
        .inbox-spinner { animation: inbox-spin 0.75s linear infinite; display: inline-block; }
        .inbox-grid {
          display: grid;
          grid-template-columns: 340px minmax(0, 1fr);
          flex: 1;
          min-height: 0;
          min-width: 0;
        }
        @media (max-width: 900px) {
          .inbox-grid { grid-template-columns: 1fr; }
          .inbox-left  { display: ${selectedConv ? "none" : "flex"} !important; }
          .inbox-right { display: ${selectedConv ? "flex" : "none"} !important; }
        }
        .inbox-row + .inbox-row { border-top: 1px solid ${C.divider}; }
      `}</style>
      <div className="inbox-grid">
        <div className="inbox-left" style={{
          display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0,
        }}>
          {leftPane}
        </div>
        <div className="inbox-right" style={{
          display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0,
        }}>
          {rightPane}
        </div>
      </div>
    </div>
  )
}
